/* eslint-disable react/prop-types */
import * as React from 'react';
import { useState } from 'react';

import { IonIcon } from '@ionic/react';
import { cloudDownloadOutline, downloadOutline } from 'ionicons/icons';
import './FileDownload.css';
import Panel from '../ui/Panel';
import { signedDownload } from '../../services/bigQueryService';

interface ProbeDownloadProps {
    staticUrl: string;
    dynamicUrls: string[];
    form : any;
    /** Signs the download when present; without it the raw url is used. */
    jobId?: string;
    onClear?: () => void;
}

const ProbeDownload: React.FC<ProbeDownloadProps> = ({ staticUrl, dynamicUrls, form, jobId }) => {
    const [isDownloading, setIsDownloading] = useState(false);
    const [error, setError] = useState('');

    /** Ask the API for a signed link, then let the browser save it. The
     *  response carries an attachment disposition, so assigning to location
     *  starts the download without leaving the page. */
    const download = async (file: 'final' | 'static') => {
        if (!jobId) {
            return;
        }
        setIsDownloading(true);
        try {
            window.location.href = await signedDownload(jobId, file);
            setError('');
        } catch (err) {
            setError((err as Error).message);
        } finally {
            setIsDownloading(false);
        }
    };

    if (!staticUrl && dynamicUrls.length === 0) {
        return null;
    }

    const downloadAllFiles = async () => {
        if (!dynamicUrls || dynamicUrls.length === 0) return;
        setIsDownloading(true);
        for (let idx = 0; idx < dynamicUrls.length; idx++) {
            const url = dynamicUrls[idx];
            try {
                const response = await fetch(url);
                if (!response.ok) throw new Error(`Failed to fetch ${url}: ${response.status}`);
                const blob = await response.blob();
                const blobUrl = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = blobUrl;
                const filename = url.split('/').pop()?.split('?')[0] || `file-${idx + 1}`;
                a.download = filename;
                document.body.appendChild(a);
                a.click();
                a.remove();
                // release memory
                URL.revokeObjectURL(blobUrl);
                // slight delay to give browser time to start the download
                await new Promise(res => setTimeout(res, 200));
            } catch (e) {
                // If fetch fails (CORS or server), attempt a direct anchor download without opening a new tab
                console.warn('download failed for', url, e);
                try {
                    const a = document.createElement('a');
                    a.href = url;
                    const filename = url.split('/').pop()?.split('?')[0] || `file-${idx + 1}`;
                    a.download = filename;
                    document.body.appendChild(a);
                    a.click();
                    a.remove();
                } catch (err) {
                    // last resort: do nothing to avoid navigating away from the page
                    console.warn('fallback download also failed for', url, err);
                }
            }
        }
        setIsDownloading(false);
    };

    return (
        <Panel
            title="Ready to download"
            subtitle="Generated by your last query"
            accent="var(--db-orange)"
            icon={<IonIcon icon={cloudDownloadOutline} />}
        >
            <table className="download-table">
                <thead>
                    <tr>
                        <th>Type</th>
                        <th className="download-table__actions">Download</th>
                    </tr>
                </thead>
                <tbody>
                    {staticUrl && (
                        <tr>
                            <td>Static data</td>
                            <td className="download-table__actions">
                                {jobId ? (
                                    <button type="button" className="download-link"
                                            onClick={() => download('static')} disabled={isDownloading}>
                                        <IonIcon icon={downloadOutline} aria-hidden="true" />
                                        Download CSV
                                    </button>
                                ) : (
                                    <a className="download-link" href={staticUrl} target="_blank" rel="noreferrer">
                                        <IonIcon icon={downloadOutline} aria-hidden="true" />
                                        Download CSV
                                    </a>
                                )}
                            </td>
                        </tr>
                    )}
                    {/* One file gets a plain link: the export bucket sends no
                        CORS header, so fetching it as a blob fails and the
                        fallback anchor is what actually saves the file. */}
                    {dynamicUrls.length === 1 && (
                        <tr>
                            <td>{form === "Probe" ? "Dynamic data" : "Query results"}</td>
                            <td className="download-table__actions">
                                {jobId ? (
                                    <button type="button" className="download-link"
                                            onClick={() => download('final')} disabled={isDownloading}>
                                        <IonIcon icon={downloadOutline} aria-hidden="true" />
                                        Download CSV
                                    </button>
                                ) : (
                                    <a className="download-link" href={dynamicUrls[0]} target="_blank" rel="noreferrer">
                                        <IonIcon icon={downloadOutline} aria-hidden="true" />
                                        Download CSV
                                    </a>
                                )}
                            </td>
                        </tr>
                    )}
                    {dynamicUrls.length > 1 && (
                        <tr>
                            <td>{form === "Probe" ? "Dynamic data" : "Query results"}</td>
                            <td className="download-table__actions">
                                <button
                                    type="button"
                                    className="download-link"
                                    onClick={downloadAllFiles}
                                    disabled={isDownloading}
                                >
                                    <IonIcon icon={downloadOutline} aria-hidden="true" />
                                    {isDownloading ? 'Downloading...' : `Download all ${dynamicUrls.length} files`}
                                </button>
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
            {error && <p className="download-error">{error}</p>}
        </Panel>
    );
};

export default ProbeDownload;